import type {
	CoursesAddBlackoutDto,
	CoursesCourseAvailabilityGridQueryDto,
	CoursesCreateBookingDto,
	CoursesMentorAvailabilityQueryDto,
	CoursesOfficeHoursCourseQueryDto,
	CoursesPreviewBlackoutConflictsDto,
	CoursesRescheduleBookingDto,
	CoursesSuggestNextSlotsQueryDto,
	CoursesUpdateBlackoutExceptionsDto,
	CoursesUpdateOfficeSettingsDto,
	CoursesUpsertAvailabilityDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import type {
	ListCourseMentorsInput,
	ListCourseMentorsOutput,
	GetMentorAvailabilityInput,
	GetMentorAvailabilityOutput,
	GetCourseAvailabilityGridInput,
	GetCourseAvailabilityGridOutput,
	SuggestNextAvailableSlotsInput,
	SuggestNextAvailableSlotsOutput,
	CreateBookingInput,
	CreateBookingOutput,
	CancelBookingInput,
	CancelBookingOutput,
	RescheduleBookingInput,
	RescheduleBookingOutput,
	GetBookingByIdInput,
	GetBookingByIdOutput,
	ListBookingsInput,
	ListMyBookingsOutput,
	ListMentorBookingsOutput,
	ListMyAvailabilityOutput,
	UpsertAvailabilityInput,
	UpsertAvailabilityOutput,
	DeleteAvailabilityInput,
	DeleteAvailabilityOutput,
	GetMyOfficeConfigOutput,
	UpdateOfficeSettingsInput,
	UpdateOfficeSettingsOutput,
	ConnectGoogleCalendarOutput,
	DisconnectGoogleCalendarOutput,
	ListMyMentorCoursesOutput,
	ListMyBlackoutsOutput,
	AddBlackoutInput,
	AddBlackoutOutput,
	UpdateBlackoutExceptionsInput,
	UpdateBlackoutExceptionsOutput,
	DeleteBlackoutInput,
	DeleteBlackoutOutput,
	PreviewBlackoutConflictsInput,
	PreviewBlackoutConflictsOutput,
} from '../../types/office-hours.types';

const TAG = 'courses:office-hours' as const;
const INVALIDATES = [TAG, 'courses:webinars', 'courses:dashboard'] as const;

export const officeHoursEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		listCourseMentors: builder.query<ListCourseMentorsOutput, ListCourseMentorsInput>({
			query: (params) => ({
				url: 'courses/office-hours/mentors',
				params: params satisfies Wire<CoursesOfficeHoursCourseQueryDto>,
			}),
			providesTags: [TAG],
		}),
		getMentorAvailability: builder.query<GetMentorAvailabilityOutput, GetMentorAvailabilityInput>({
			query: (params) => ({
				url: 'courses/office-hours/availability',
				params: params satisfies Wire<CoursesMentorAvailabilityQueryDto>,
			}),
			providesTags: [TAG],
		}),
		getCourseAvailabilityGrid: builder.query<
			GetCourseAvailabilityGridOutput,
			GetCourseAvailabilityGridInput
		>({
			query: (params) => ({
				url: 'courses/office-hours/availability-grid',
				params: params satisfies Wire<CoursesCourseAvailabilityGridQueryDto>,
			}),
			providesTags: [TAG],
		}),
		suggestNextAvailableSlots: builder.query<
			SuggestNextAvailableSlotsOutput,
			SuggestNextAvailableSlotsInput
		>({
			query: (params) => ({
				url: 'courses/office-hours/next-slots',
				params: params satisfies Wire<CoursesSuggestNextSlotsQueryDto>,
			}),
			providesTags: [TAG],
		}),
		createBooking: builder.mutation<CreateBookingOutput, CreateBookingInput>({
			query: (body) => ({
				url: 'courses/office-hours/bookings',
				method: 'POST',
				body: body satisfies Wire<CoursesCreateBookingDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		cancelBooking: builder.mutation<CancelBookingOutput, CancelBookingInput>({
			query: ({ bookingId }) => ({
				url: `courses/office-hours/bookings/${encodeURIComponent(bookingId)}/cancel`,
				method: 'POST',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		rescheduleBooking: builder.mutation<RescheduleBookingOutput, RescheduleBookingInput>({
			query: ({ bookingId, ...body }) => ({
				url: `courses/office-hours/bookings/${encodeURIComponent(bookingId)}/reschedule`,
				method: 'POST',
				body: body satisfies Wire<CoursesRescheduleBookingDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		getBookingById: builder.query<GetBookingByIdOutput, GetBookingByIdInput>({
			query: ({ bookingId }) => ({
				url: `courses/office-hours/bookings/${encodeURIComponent(bookingId)}`,
			}),
			providesTags: [TAG],
		}),
		listMyBookings: builder.query<ListMyBookingsOutput, ListBookingsInput>({
			query: (params) => ({
				url: 'courses/office-hours/bookings/mine',
				params: params ?? undefined,
			}),
			providesTags: [TAG],
		}),
		listMentorBookings: builder.query<ListMentorBookingsOutput, ListBookingsInput>({
			query: (params) => ({
				url: 'courses/office-hours/bookings/as-mentor',
				params: params ?? undefined,
			}),
			providesTags: [TAG],
		}),
		listMyAvailability: builder.query<ListMyAvailabilityOutput, void>({
			query: () => ({ url: 'courses/office-hours/my-availability' }),
			providesTags: [TAG],
		}),
		upsertAvailability: builder.mutation<UpsertAvailabilityOutput, UpsertAvailabilityInput>({
			query: (body) => ({
				url: 'courses/office-hours/my-availability',
				method: 'PUT',
				body: body satisfies Wire<CoursesUpsertAvailabilityDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		deleteAvailability: builder.mutation<DeleteAvailabilityOutput, DeleteAvailabilityInput>({
			query: ({ id }) => ({
				url: `courses/office-hours/my-availability/${encodeURIComponent(id)}`,
				method: 'DELETE',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		getMyOfficeConfig: builder.query<GetMyOfficeConfigOutput, void>({
			query: () => ({ url: 'courses/office-hours/my-config' }),
			providesTags: [TAG],
		}),
		updateOfficeSettings: builder.mutation<UpdateOfficeSettingsOutput, UpdateOfficeSettingsInput>({
			query: (body) => ({
				url: 'courses/office-hours/my-config',
				method: 'PUT',
				body: body satisfies Wire<CoursesUpdateOfficeSettingsDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		connectGoogleCalendar: builder.mutation<ConnectGoogleCalendarOutput, void>({
			query: () => ({ url: 'courses/office-hours/google/connect', method: 'POST' }),
		}),
		disconnectGoogleCalendar: builder.mutation<DisconnectGoogleCalendarOutput, void>({
			query: () => ({ url: 'courses/office-hours/google/disconnect', method: 'POST' }),
			invalidatesTags: [...INVALIDATES],
		}),
		listMyMentorCourses: builder.query<ListMyMentorCoursesOutput, void>({
			query: () => ({ url: 'courses/office-hours/mentor-courses' }),
			providesTags: [TAG],
		}),
		listMyBlackouts: builder.query<ListMyBlackoutsOutput, void>({
			query: () => ({ url: 'courses/office-hours/blackouts' }),
			providesTags: [TAG],
		}),
		addBlackout: builder.mutation<AddBlackoutOutput, AddBlackoutInput>({
			query: (body) => ({
				url: 'courses/office-hours/blackouts',
				method: 'POST',
				body: body satisfies Wire<CoursesAddBlackoutDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		updateBlackoutExceptions: builder.mutation<
			UpdateBlackoutExceptionsOutput,
			UpdateBlackoutExceptionsInput
		>({
			query: ({ id, ...body }) => ({
				url: `courses/office-hours/blackouts/${encodeURIComponent(id)}/exceptions`,
				method: 'PATCH',
				body: body satisfies Wire<CoursesUpdateBlackoutExceptionsDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		deleteBlackout: builder.mutation<DeleteBlackoutOutput, DeleteBlackoutInput>({
			query: ({ id }) => ({
				url: `courses/office-hours/blackouts/${encodeURIComponent(id)}`,
				method: 'DELETE',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		// A read (the original used it with `useQuery`): a POST that only computes overlaps, so
		// it is a query that provides the tag instead of a mutation that would invalidate it.
		previewBlackoutConflicts: builder.query<
			PreviewBlackoutConflictsOutput,
			PreviewBlackoutConflictsInput
		>({
			query: (body) => ({
				url: 'courses/office-hours/blackouts/preview-conflicts',
				method: 'POST',
				body: body satisfies Wire<CoursesPreviewBlackoutConflictsDto>,
			}),
			providesTags: [TAG],
		}),
	}),
});

export const {
	useListCourseMentorsQuery,
	useLazyListCourseMentorsQuery,
	useGetMentorAvailabilityQuery,
	useLazyGetMentorAvailabilityQuery,
	useGetCourseAvailabilityGridQuery,
	useLazyGetCourseAvailabilityGridQuery,
	useSuggestNextAvailableSlotsQuery,
	useLazySuggestNextAvailableSlotsQuery,
	useCreateBookingMutation,
	useCancelBookingMutation,
	useRescheduleBookingMutation,
	useGetBookingByIdQuery,
	useLazyGetBookingByIdQuery,
	useListMyBookingsQuery,
	useLazyListMyBookingsQuery,
	useListMentorBookingsQuery,
	useLazyListMentorBookingsQuery,
	useListMyAvailabilityQuery,
	useLazyListMyAvailabilityQuery,
	useUpsertAvailabilityMutation,
	useDeleteAvailabilityMutation,
	useGetMyOfficeConfigQuery,
	useLazyGetMyOfficeConfigQuery,
	useUpdateOfficeSettingsMutation,
	useConnectGoogleCalendarMutation,
	useDisconnectGoogleCalendarMutation,
	useListMyMentorCoursesQuery,
	useLazyListMyMentorCoursesQuery,
	useListMyBlackoutsQuery,
	useLazyListMyBlackoutsQuery,
	useAddBlackoutMutation,
	useUpdateBlackoutExceptionsMutation,
	useDeleteBlackoutMutation,
	usePreviewBlackoutConflictsQuery,
	useLazyPreviewBlackoutConflictsQuery,
} = officeHoursEndpoints;
